/**
 * TrialStatusBanner
 *
 * App-wide notice (same surface rules as GlobalLowBalanceBanner) that keeps
 * trial users oriented: how many days their trial has left and how much
 * credit remains — denominated in PRO Work Units (pr0) with the logo-zero
 * mark (2026-08-14 owner directive; supersedes the dollar copy). Trial users
 * bypass the low-balance banner entirely (entitlementBypass), so this is
 * their one persistent status surface.
 *
 * Rendering: registers with the surrounding GlobalBannerCenter (the ONE
 * global top strip; rotates when several notices are eligible) and renders
 * nothing itself. Standalone (no BannerBusContext above), it falls back to
 * rendering its legacy strip directly.
 *
 * States:
 *   - Active, comfortable  → calm informational notice. Dismissible, but the
 *     dismissal expires at 5:00 AM local the next morning (bannerDismissal
 *     daily policy) — the banner returns every day.
 *   - Expiring soon (≤3d) or credit nearly used → warn, not dismissible,
 *     points the user to their Prest0 contact to activate a full account.
 *   - Expired → critical, not dismissible: trial accounts cannot add
 *     credits — their Prest0 contact activates the full account.
 */

import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { useBannerSurface } from '../common/bannerSurface';
import { useWorkspaceTrialStatus } from '../../hooks/useTrialStatus';
import {
  BannerBusContext,
  useRegisterBanner,
  type BannerTone,
} from '../common/GlobalBannerCenter';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from '../common/bannerDismissal';
import Pr0Unit from '../common/Pr0Unit';
import { usdToWu, formatWu } from '../../config/workUnits';

/**
 * v2 (2026-08-14): daily-expiring localStorage dismissal (5 AM policy).
 * The old `prest0n_trialbanner_dismissed` sessionStorage flag is retired.
 */
const DISMISS_KEY = 'prest0n_trialbanner_dismissed_v2';

type Tone = BannerTone;

/* Legacy standalone-fallback palette (pre-center strip). The bus path uses
   the GlobalBannerCenter cream/gold/ink tones instead. */
const TONE_STYLES: Record<Tone, { bg: string; fg: string; border: string; btnBg: string }> = {
  info: { bg: '#E8F0FE', fg: '#1E3A5F', border: 'rgba(30,58,95,0.18)', btnBg: '#1d4ed8' },
  warn: { bg: '#fde68a', fg: '#78350f', border: 'rgba(120,53,15,0.2)', btnBg: '#b45309' },
  critical: { bg: '#fecaca', fg: '#7f1d1d', border: 'rgba(153,27,27,0.25)', btnBg: '#dc2626' },
};

/**
 * In normal document flow (rendered directly below the app Header in
 * App.tsx) — the strip occupies its own band and pushes page content down
 * instead of overlaying the header (owner report 2026-07-31).
 */
const Strip = styled.div<{ $tone: Tone }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => TONE_STYLES[p.$tone].fg};
  background: ${(p) => TONE_STYLES[p.$tone].bg};
  border-bottom: 1px solid ${(p) => TONE_STYLES[p.$tone].border};
`;

const Message = styled.span`
  max-width: 80vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 640px) {
    white-space: normal;
    line-height: 1.35;
  }
`;

const ContactButton = styled.a<{ $tone: Tone }>`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${(p) => TONE_STYLES[p.$tone].btnBg};
  border: none;
  text-decoration: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

const DismissButton = styled.button`
  flex: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  padding: 2px 4px;
  line-height: 0;
  &:hover { opacity: 1; }
  svg { width: 16px; height: 16px; }
`;

export const TrialStatusBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  // Keyed to the ACTIVE WORKSPACE (owner report 2026-08-17): the banner shows
  // the same trial numbers as the wallet page the user is in — the acting
  // account's trial state + paying-wallet balance, never the signed-in
  // member's native account. In a non-trial workspace the banner stays hidden
  // even when the member's own account is on a trial.
  const trial = useWorkspaceTrialStatus();
  const onAppSurface = useBannerSurface();

  // Daily dismissal (calm state only): the X holds until 5:00 AM local the
  // next morning, then the banner returns (owner directive 2026-08-14).
  const [dismissedAtIso, setDismissedAtIso] = useState<string | null>(() =>
    readDailyDismissal(DISMISS_KEY),
  );
  const dismissed = isDismissalActive(dismissedAtIso);

  const eligible = !trial.isLoading && trial.isTrialUser && onAppSurface;
  const creditsExhausted = trial.isActive && trial.creditRemainingCents <= 0;
  const urgent = trial.isExpired || trial.isExpiringSoon || creditsExhausted;

  // Calm state hides only while a same-morning dismissal holds; urgent
  // states always show.
  const visible = eligible && (urgent || !dismissed);

  const tone: Tone = trial.isExpired ? 'critical' : urgent ? 'warn' : 'info';
  // ONE number everywhere (task #76): the banner prints the workspace's
  // actual wallet pr0 — identical to the wallet page — falling back to the
  // standard-rate conversion only until the wallet snapshot lands.
  const wuRemaining = trial.liveWalletWu !== null
    ? formatWu(trial.liveWalletWu)
    : formatWu(usdToWu(trial.creditRemainingCents / 100));
  const days = trial.daysRemaining;
  const daysText = days !== null ? `${days} day${days === 1 ? '' : 's'}` : null;

  // Copy is lowercase by convention (official prest0 style — matches wallet
  // + redemption surfaces); dates keep their natural capitalization. Amounts
  // speak pr0 with the logo-zero mark — never dollars (2026-08-14).
  let message: React.ReactNode;
  if (trial.isExpired) {
    message =
      'your trial has ended. trial accounts can’t top up Work Units — your prest0 point of contact will activate your full account.';
  } else if (creditsExhausted) {
    message = `you’ve used all of your trial credit${daysText ? ` (trial ends in ${daysText})` : ''}. trial credits can’t be topped up — contact your prest0 point of contact to activate your full account.`;
  } else if (trial.isExpiringSoon) {
    message = (
      <>
        your trial ends in {daysText}
        {trial.endsOn ? ` (${trial.endsOn})` : ''} — {wuRemaining} <Pr0Unit /> remaining. to keep
        going after that, contact your prest0 point of contact.
      </>
    );
  } else {
    // Calm state. No top-up link on purpose: trials can't top up.
    message = (
      <>
        free trial — {wuRemaining} <Pr0Unit /> remaining
        {daysText ? ` · ends in ${daysText}` : ''}
        {trial.endsOn ? ` on ${trial.endsOn}` : ''}
      </>
    );
  }

  const handleDismiss = () => {
    const now = new Date();
    writeDailyDismissal(DISMISS_KEY, now);
    setDismissedAtIso(now.toISOString());
  };

  const content = (
    <>
      <Message>{message}</Message>
      {urgent && (
        <ContactButton $tone={tone} href="/subscription">
          activate your account
        </ContactButton>
      )}
    </>
  );

  useRegisterBanner(
    visible
      ? {
          id: 'trial-status',
          tone,
          content,
          dismissible: !urgent,
          onDismiss: !urgent ? handleDismiss : undefined,
        }
      : null,
  );

  // Inside a GlobalBannerCenter the center owns the strip.
  if (bus) return null;

  // ── Legacy standalone fallback (no center above) ──────────────────────────
  if (!visible) return null;

  return (
    <Strip $tone={tone} role="status" aria-live="polite" data-testid="trial-status-banner">
      {content}
      {!urgent && (
        <DismissButton type="button" onClick={handleDismiss} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DismissButton>
      )}
    </Strip>
  );
};

export default TrialStatusBanner;
